import {toast} from 'react-toastify';
import {observer} from 'mobx-react-lite';
import React, {FC, useState} from 'react';
import {switchProfile, VerdocsEndpoint} from '@verdocs/js-sdk';
import {BuildingOffice2Icon, PlusIcon} from '@heroicons/react/24/outline';
import {queryClient, useOrganization, useOrganizationUsage, useProfiles} from '../../react-sdk/queries';
import {FlexFill, HelpDialog, PageTitle, Spinner} from '../../Components';
import {CreateChildDialog} from '../../Dialogs';
import {OrgCard} from './Components';
import {AppState} from '../../AppState';

const CreateChildButton: FC<{onClick: () => void}> = ({onClick}) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700">
    <PlusIcon className="h-4 w-4" />
    Create Child Organization
  </button>
);

export const Hierarchy: FC = observer(() => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const profilesQuery = useProfiles();
  const orgQuery = useOrganization(organizationId);
  const usageQuery = useOrganizationUsage(organizationId);
  const [creating, setCreating] = useState(false);

  const org = orgQuery.data;
  const usage = usageQuery.data || {};

  const getProfileForOrg = (orgId: string | null | undefined) => (profilesQuery.data || []).find((p) => p.organization_id === orgId);

  const handleSwitchToOrg = async (targetOrgId: string) => {
    const profile = getProfileForOrg(targetOrgId);
    if (!profile) {
      return;
    }

    try {
      const r = await switchProfile(VerdocsEndpoint.getDefault(), profile.id);
      VerdocsEndpoint.getDefault().setToken(r.access_token);
      queryClient.clear();
      window.location.reload();
    } catch (e: any) {
      console.log('Error switching profiles', e);
      toast.error('Unable to switch profiles: ' + e.message);
    }
  };

  if (!org) {
    return (
      <div className="w-full max-w-[1000px]">
        <PageTitle>Organization Hierarchy</PageTitle>
        {orgQuery.isError ? (
          <div className="text-sm text-gray-500">Unable to load organization details. Please try again later.</div>
        ) : (
          <Spinner mode="dark" />
        )}
      </div>
    );
  }

  const children = org.children || [];

  return (
    <div className="w-full max-w-[1000px]">
      <PageTitle>
        Organization Hierarchy
        <FlexFill />
        <HelpDialog title="Organization Hierarchy">
          <p>
            Child organizations are separate, fully isolated workspaces managed by your organization. Each one has its own members,
            templates, branding, and API keys, which makes them a good fit for customers or business units that operate independently.
          </p>
          <p>
            Switching to an organization changes your active profile, so everything you see and do applies to that organization until you
            switch back. Currently, only single-level relationships are supported.
          </p>
        </HelpDialog>
      </PageTitle>

      {org.parent ? (
        <>
          <p className="mb-6 max-w-[640px] text-sm text-gray-500">
            "{org.name}" is a child organization of "{org.parent.name}". Switch to the parent organization to manage its settings and other
            child organizations.
          </p>

          <div className="max-w-[480px]">
            <OrgCard
              org={org.parent}
              badge="Parent organization"
              canSwitch={!!getProfileForOrg(org.parent_id)}
              onSwitch={() => handleSwitchToOrg(org.parent_id || '')}
            />
          </div>
        </>
      ) : (
        <>
          <p className="mb-6 max-w-[640px] text-sm text-gray-500">
            Give each customer or business unit its own fully isolated workspace, managed from {org.name}.
          </p>

          <div className="mb-4 flex flex-wrap items-center gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Child Organizations</h2>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{children.length}</span>
            <FlexFill />
            {children.length > 0 && <CreateChildButton onClick={() => setCreating(true)} />}
          </div>

          {children.length > 0 ? (
            <div className="grid grid-cols-1 items-start gap-4 laptop:grid-cols-2">
              {children.map((child) => (
                <OrgCard
                  key={child.id}
                  org={child}
                  stats={{
                    members: child.profiles?.length ?? 0,
                    envelopes: usage[child.id]?.envelope ?? 0,
                    templates: usage[child.id]?.template ?? 0,
                  }}
                  canSwitch={!!getProfileForOrg(child.id)}
                  onSwitch={() => handleSwitchToOrg(child.id)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 px-6 py-16 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50">
                <BuildingOffice2Icon className="h-7 w-7 text-indigo-500" />
              </div>
              <p className="mb-1 font-medium text-gray-700">No child organizations yet</p>
              <p className="mb-6 max-w-[420px] text-sm text-gray-400">
                Create one for each customer or business unit you manage. Every child organization gets its own members, templates,
                branding, and API keys.
              </p>
              <CreateChildButton onClick={() => setCreating(true)} />
            </div>
          )}
        </>
      )}

      {creating && (
        <CreateChildDialog
          onClose={() => {
            setCreating(false);
            profilesQuery.refetch();
            orgQuery.refetch();
          }}
        />
      )}
    </div>
  );
});
